import React, { FC, useCallback, useState } from 'react';

import { AxiosResponse } from 'axios';

// components
import TextInput from '../../../components/inputs/textInput';
import AampeItalicText from '../../../components/shared/AampeText/AampeItalicText';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../constants/aampeTheme';
import ICONS from '../../../constants/icons';

// models
import { FormulaCreateModel } from '../../../models/formula.model';

import FormulaNameTooltip from '../FormulaNameTooltip';

interface FormulaInputTextProps {
    formulaName: string;
    onTitleChangeMutation: (
        updates: FormulaCreateModel,
        onSuccess?: ((value?: number) => void) | undefined,
    ) => Promise<AxiosResponse<any, any>> | null | undefined | void;
    isDisabled?: boolean;
}

const FormulaNameInput: FC<React.PropsWithChildren<FormulaInputTextProps>> = ({
    formulaName,
    onTitleChangeMutation,
    isDisabled,
}) => {
    const [value, setValue] = useState<string>(formulaName);

    const handleUpdate = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value);
            if (e.target.value !== formulaName) {
                onTitleChangeMutation({
                    name: e.target.value,
                });
            }
        },
        [formulaName, onTitleChangeMutation],
    );

    return (
        <TitleContainer>
            <TitleWithInfo>
                <TitleName>
                    Message name <AampeItalicText text={'(Required)'} />
                </TitleName>
                <FormulaNameTooltip
                    tooltipPlacement="right"
                    text={formulaName || 'Unnamed'}
                >
                    <ICONS.info size={16} color={aampeTheme.colors.neutral09} />
                </FormulaNameTooltip>
            </TitleWithInfo>

            <StyledTextField
                placeholder="Give a name for your own identification"
                value={value ?? formulaName}
                wrapperStyle={{ padding: aampeTheme.spacing(0) }}
                onChange={handleUpdate}
                name="formula_name"
                isDisabled={isDisabled}
                maxLength={250}
            />
        </TitleContainer>
    );
};

export default FormulaNameInput;

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: aampeTheme.spacing(1),
});
const TitleName = styled.span({
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
    color: aampeTheme.colors.gray800,
});

const TitleWithInfo = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
});

const StyledTextField = styled(TextInput)({
    width: '100%',
    padding: aampeTheme.spacing(0),
    '& .MuiInputBase-root': {
        backgroundColor: aampeTheme.colors.white,
        borderRadius: aampeTheme.borderRadius.default,
        borderColor: aampeTheme.colors.bordersWG01,
        minHeight: '38px',
        '&:hover fieldset': {
            borderColor: aampeTheme.colors.solidA01,
        },
    },
});
